import { ChangeEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Alert, Button, Input, StrongModalText } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { useModal, usePermissions } from '@hooks';
import { parseAmount } from '@utils';
import styles from './styles';
import { BALANCE_RETRIEVE_VALIDATION } from '../../../screens/employeeDetails/tabs/productsTab/constants';

interface RetrieveBalanceModalProps {
  totalBalance: number;
  submit: (amount: number, fullRecovery: boolean) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  isAreaRecover?: boolean;
  areaName?: string;
}
/**
 * Renders a modal with the input fields and recover balance
 * @param {RetrieveBalanceModalProps} props
 * @param  props.isOpen is modal is or not visible
 * @param  props.onClose function to execuete after modal close
 * @param  props.isLoading flag indicating whether the request is loading
 * @param  props.handleSubmit function to set clients data
 */
const RetrieveBalanceModal = ({
  submit,
  isOpen,
  onClose,
  isLoading,
  totalBalance,
  isAreaRecover = false,
  areaName,
}: RetrieveBalanceModalProps) => {
  const [fullRecovery, setFullRecovery] = useState(true);
  const {
    closeModal: closeConfirmModal,
    open: isConfirmModalOpen,
    openModal: openConfirmAreaModal,
  } = useModal();
  const { t } = useTranslation();
  const isDemo = usePermissions([PERMISSIONS.demoUpdateRecoveryBalance]);
  /**
   * On close modal handler
   */
  const onCloseHandler = () => {
    onClose();
    setFullRecovery(true);
    setErrors({});
    resetForm();
  };

  /**
   * Call the function that updates the balance.
   */
  const onSubmit = async () => {
    try {
      const amount = fullRecovery ? totalBalance : Number.parseFloat(parsedAmount);
      await submit(amount, fullRecovery);
    } finally {
      closeConfirmModal();
      onCloseHandler();
    }
  };

  const { handleSubmit, errors, values, handleChange, setErrors, resetForm } = useFormik({
    initialValues: { amount: '' },
    validationSchema: BALANCE_RETRIEVE_VALIDATION({ totalBalance, fullRecovery }),
    onSubmit,
  });
  const parsedAmount = values.amount.replace(',', '.');

  /**
   * Handle the change of the partialRecovery switch
   * @param  isPartial
   */
  const handlePartialRecoveryChange = (isPartial: boolean) => {
    const isFullRecovery = !isPartial;

    if (isFullRecovery) {
      setErrors({});
      resetForm();
    }

    setFullRecovery(isFullRecovery);
  };

  /**
   * Handles a change in the value
   * @param {ChangeEvent} event
   */
  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    value = value
      .replace(/\./g, ',')
      .replace(/[^0-9,]/g, '')
      .split(',')
      .slice(0, 2)
      .join(',');

    handleChange('amount')(value);
  };

  const invalidAmount = fullRecovery ? false : Boolean(errors.amount);
  const disableButton = isLoading || invalidAmount || isDemo;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>
        {t(
          isAreaRecover
            ? 'CLIENTS:REASSIGN_BALANCE_MODAL:REASSIGN_BALANCE_TITLE'
            : 'EMPLOYEES:DETAIL:RETRIEVE_BALANCE_TITLE'
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={styles.modalContent} textAlign='center'>
          <FormControlLabel
            sx={styles.formSwitch}
            label={t('EMPLOYEES:DETAIL:RETRIEVE_PARTIAL_AMOUNT')}
            control={
              <Switch
                checked={!fullRecovery}
                onChange={(e, isPartial) => handlePartialRecoveryChange(isPartial)}
              />
            }
          />
          <Input
            variant='outlined'
            size='small'
            inputProps={{
              startAdornment: <InputAdornment position='start'>$</InputAdornment>,
              placeholder: fullRecovery
                ? parseAmount(totalBalance)
                : t('EMPLOYEES:DETAIL:RETRIEVE_BALANCE_AMOUNT_PLACEHOLDER'),
              type: 'text',
            }}
            error={Boolean(errors.amount)}
            value={values.amount}
            onChange={handleAmountChange}
            disabled={fullRecovery}
            helperText={
              fullRecovery
                ? t(
                    isAreaRecover
                      ? 'CLIENTS:REASSIGN_BALANCE_MODAL:REASSIGN_BALANCE_TOTAL_HELPER'
                      : 'EMPLOYEES:DETAIL:RETRIEVE_BALANCE_TOTAL_HELPER',
                    {
                      balance: parseAmount(totalBalance),
                    }
                  )
                : errors.amount
            }
          />
          <DialogActions>
            <Button text={t('COMMON:CANCEL')} color='error' onClick={onCloseHandler} />
            <Button
              variant='contained'
              color='primary'
              type='submit'
              isLoading={isLoading}
              disabled={disableButton}
              onClick={isAreaRecover ? openConfirmAreaModal : handleSubmit}
              text={t('COMMON:CONFIRM')}
            />
          </DialogActions>
          <Alert
            variant='warning'
            modalVisible={isConfirmModalOpen}
            handleClose={closeConfirmModal}
            handleSubmit={onSubmit}
            closeButtonText='COMMON:CANCEL'
            title={t('DISPERSIONS:MODAL_TITLE')}
            submitLoading={isLoading}
            content={
              <Typography color='gray.90'>
                <Trans
                  i18nKey={t(
                    `CLIENTS:REASSIGN_BALANCE_MODAL:CONFIRMATION_DESCRIPTION_${
                      fullRecovery ? 'TOTAL' : 'PARCIAL'
                    }_REASSIGMENT`
                  )}
                  components={{ bold: <StrongModalText /> }}
                  values={{
                    amount: parseAmount(
                      fullRecovery ? totalBalance : Number.parseFloat(parsedAmount)
                    ),
                    areaName: areaName,
                  }}
                />
              </Typography>
            }
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RetrieveBalanceModal;
